import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { MessageId } from "@domain/intl/useTranslation";

import type { AgentDetailItem, AgentStep, ReportLine } from "../runner/types";
import { selectUnexported } from "./draftSelection";

const toDetail = (
    r: GQL.ITransactionExportResult,
    kind: GQL.ITransactionExportKind,
    product: GQL.IProductKey,
): AgentDetailItem => {
    const ref = {
        kind: kind === GQL.ITransactionExportKind.Bank ? ("bankTransaction" as const) : ("kasseTransaction" as const),
        id: String(r.transactionId),
    };
    const target = r.recordId ? { target: { kind: "record" as const, id: String(r.recordId), product } } : {};
    const line = (textId: MessageId, values?: { message: string }) => ({
        textId,
        ...(values ? { values } : {}),
    });
    switch (r.outcome) {
        case GQL.ITransactionExportOutcome.Created:
            return { group: "done", ref, ...target, outcome: line("app.agent.details.txDrafts.created") };
        case GQL.ITransactionExportOutcome.AlreadyExported:
            return { group: "untouched", ref, ...target, outcome: line("app.agent.details.txDrafts.alreadyExported") };
        case GQL.ITransactionExportOutcome.Invalid:
            return {
                group: "refused",
                ref,
                outcome: r.message
                    ? line("app.agent.details.txDrafts.invalid", { message: r.message })
                    : line("app.agent.details.txDrafts.invalidNoMessage"),
            };
        default:
            return { group: "refused", ref, outcome: line("app.agent.details.txDrafts.notFound") };
    }
};

export const draftsFromTransactionsStep: AgentStep = {
    id: "draftsFromTransactions",
    async run(ctx, io) {
        const kinds = [
            {
                kind: GQL.ITransactionExportKind.Bank,
                product: GQL.IProductKey.Bank,
                transactions: () => ctx.bankTransactions().map((tx) => ({ id: String(tx.id), date: tx.bookingDate })),
                thinkingId: "app.agent.txDrafts.bankThinking",
                doneId: "app.agent.txDrafts.bankDone",
                reportId: "app.agent.report.txDraftsBank",
            },
            {
                kind: GQL.ITransactionExportKind.Kasse,
                product: GQL.IProductKey.Kb,
                transactions: () => ctx.cashRegisterTransactions().map((tx) => ({ id: String(tx.id), date: tx.date })),
                thinkingId: "app.agent.txDrafts.kasseThinking",
                doneId: "app.agent.txDrafts.kasseDone",
                reportId: "app.agent.report.txDraftsKasse",
            },
        ] as const;

        const work: ((typeof kinds)[number] & { ids: string[] })[] = [];
        for (const k of kinds) {
            if (io.isCancelled()) break;
            const ids = selectUnexported(k.transactions(), ctx.exportedTransactionIds(k.kind), ctx.year);
            if (ids.length === 0) continue;
            if (!ctx.canWrite(k.product)) {
                io.narrateSkipped(k.product);
                continue;
            }
            work.push({ ...k, ids });
        }
        if (work.length === 0) return { status: "nothing" };

        const approved = await io.approve({
            textId: "app.agent.txDrafts.approve.question",
            values: { count: work.reduce((sum, w) => sum + w.ids.length, 0) },
            goLabelId: "app.agent.txDrafts.approve.go",
            learnMoreId: "app.agent.txDrafts.learnMore",
        });
        if (!approved) return { status: "skipped", byUser: true };

        const summary: ReportLine[] = [];
        const details: AgentDetailItem[] = [];
        let invalid = 0;
        let notFound = 0;
        for (const { kind, product, ids, thinkingId, doneId, reportId } of work) {
            if (io.isCancelled()) break;
            const results = await withThinking(io, thinkingId, () => ctx.exportTransactions(kind, ids));
            const kindDetails = results.map((r) => toDetail(r, kind, product));
            details.push(...kindDetails);
            const createdResults = results.filter((r) => r.outcome === GQL.ITransactionExportOutcome.Created);
            for (const r of createdResults) if (r.recordId) ctx.created.recordIds.push(r.recordId);
            invalid += results.filter((r) => r.outcome === GQL.ITransactionExportOutcome.Invalid).length;
            notFound += results.filter((r) => r.outcome === GQL.ITransactionExportOutcome.NotFound).length;
            if (createdResults.length > 0) {
                const values = { count: createdResults.length };
                io.post({
                    kind: "done",
                    textId: doneId,
                    values,
                    details: kindDetails,
                });
                summary.push({ textId: reportId, values });
            }
        }

        const open: ReportLine[] = [];
        if (invalid > 0) open.push({ textId: "app.agent.report.txDraftsInvalid", values: { count: invalid } });
        if (notFound > 0) open.push({ textId: "app.agent.report.txDraftsNotFound", values: { count: notFound } });
        return { status: summary.length > 0 || open.length > 0 ? "done" : "nothing", summary, open, details };
    },
};
